import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { BasePdpImplementationProps, SimpleCardPdpModalProps as ProductDetailsPageProps, StationeryProductSelector, StationeryTemplatePDP } from './ProductDetailsPage.types';
import { enrichCardTelemetryExtraInfo, useCardTelemetry } from '@apps/card/Card.telemetry';
import { isBasicDigitalV2 } from '@apps/card/components/ProductUIConfig/isBasicDigitalV2';
import { CardFormat } from './constants';
import { useStickyCustomizeButton } from './useStickyCustomizeButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

import type { DigitalDesignTemplate } from './usePdpData';
import { usePdpData } from './usePdpData';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { PdpLayout } from './components/PdpLayout/PdpLayout';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCustomizeClickHandler } from './useCustomizeClickHandler';
import type { Format } from '../DesignsGallery/DesignsGallery.types';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { PaperPdp } from './PaperPdp';
import { PremiumDigitalPdp } from './PremiumDigitalPdp';
import { BasicDigitalPdp } from './BasicDigitalPdp';
import { BasicDigitalV2Pdp } from './BasicDigitalV2Pdp';
import { isFoilTemplate } from '../DesignsGallery/utils';
import { wrapWithStationeryProductDetailsHealthProvider } from './ProductDetails.health';
import { PdpSeo } from './PdpSeo';
import { PdpColorwayProvider } from './PdpColorwayContext';
import { convertCardFormatToProductType, convertProductTypeToCardFormat } from './utils';
import { StationeryProductPricingContextProvider } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { withWindow } from '@shared/utils/withWindow';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { FREE_DIGITAL_DUMMY_PRODUCT } from '@apps/card/components/ProductUIConfig/freeDigitalProduct';
import { useIsBasicDigitalV2Enabled } from './useIsBasicDigitalV2Enabled';
import { useCanUseFreeCards } from '@shared/utils/eventInfo/useCanUseFreeCards';
import { useHistory } from '@react-router';
// Own module paths, not the `@shared/utils/eventInfo` barrel — same reason `useCanUseFreeCards`
// documents: a spec that mocks the barrel with only `useEventInfo` would get `undefined` here.
import { isEventReadPending } from '@shared/utils/eventInfo/isEventReadPending';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';
import { useCardsRouterContext } from '@apps/card/Card.routes';

interface RootProps {
  eventId?: string;
  // Null for digital-only themes: no printable paper template exists (e.g. its
  // StationeryTemplate is disabled), only a Joy digital design. Rendered as a
  // free basic-digital PDP.
  template: StationeryTemplatePDP | null;
  digitalDesignTemplate: DigitalDesignTemplate | null;
  templateCategory: StationeryTemplateCategoryEnum;
  themeId: string;
  designName: string;
  isBasicDigitalV2Enabled: boolean;
  onCustomizeCardClickOverride?: ProductDetailsPageProps['onCustomizeCardClickOverride'];
}

const Root = (props: RootProps) => {
  const { eventId, onCustomizeCardClickOverride, template, templateCategory, digitalDesignTemplate, themeId, designName, isBasicDigitalV2Enabled } = props;

  const supportedTemplateFormats: Format = template && digitalDesignTemplate ? 'paperAndDigital' : template ? 'paper' : 'digital';

  const queryParams = useQueryParamHelper();
  const selectedFormat = (queryParams.getValueString('format') as CardFormat | undefined) ?? CardFormat.paper;

  const { buttonInteracted } = useCardTelemetry({ page: 'productPdpModal' });

  // No printable template (digital-only theme) → basic digital is the only option.
  const hasBasicDigital = supportedTemplateFormats !== 'paper' && !template?.premium;
  const canUseFreeCards = useCanUseFreeCards();
  const products = useMemo(() => {
    // Basic Digital V2 is a real `stationeryProduct`, linked per template server-side, so
    // the flag no longer decides whether it EXISTS in the list -- it decides whether we
    // show it. Filtering rather than appending is what keeps the flag a kill switch:
    // appending would let the seeded product render with the flag off. Resolved by the
    // parent, which holds the skeleton until the flag is in -- `selectedProductId` is
    // seeded once, so a late arrival would strand a `?format=basicDigitalV2` deep link.
    const templateProducts = isBasicDigitalV2Enabled ? (template?.products ?? []) : (template?.products ?? []).filter(p => !isBasicDigitalV2(p));
    // Legacy Basic Digital and Basic Digital V2 are mutually exclusive: V2 replaces the
    // legacy dummy when shown. `isBasicDigitalV2Enabled` is false for templates without a
    // seeded V2 product, so those (and digital-only themes) keep the legacy option.
    // MGT-109: legacy Basic Digital is the free ecard, which a new-stack wedding cannot
    // send, so it is dropped there -- but only while a real product is left to show. A
    // digital-only design has nothing else and is redirected away before Root mounts (see
    // `hideDigitalOnlyDesign` below); the guard here keeps an empty list unreachable.
    const dropLegacyForNewStack = !canUseFreeCards && templateProducts.length > 0;
    const showLegacyBasicDigital = hasBasicDigital && !isBasicDigitalV2Enabled && !dropLegacyForNewStack;
    return [...templateProducts, ...(showLegacyBasicDigital ? [FREE_DIGITAL_DUMMY_PRODUCT] : [])];
  }, [canUseFreeCards, hasBasicDigital, isBasicDigitalV2Enabled, template?.products]);

  const [selectedProductId, setSelectedProductId] = useState(() => {
    // `format` may be a raw productType (magnet/napkin/sign — see PdpFormatParam):
    // those render through the paper PDP, so a CardFormat alone cannot carry the
    // selection across a size navigation. Match the raw type first.
    const byRawProductType = products.find(p => p.productType === selectedFormat);
    if (byRawProductType) return byRawProductType.id;
    const productType = convertCardFormatToProductType(selectedFormat);
    const byProductType = products.find(p => p.productType === productType);
    if (byProductType) return byProductType.id;
    // Only one free digital option is ever listed, so a link to the other one lands on
    // whichever is shown instead of falling back to paper.
    if (selectedFormat === CardFormat.digital || selectedFormat === CardFormat.basicDigitalV2) {
      const freeDigital = products.find(p => p.productType === 'digital' || isBasicDigitalV2(p));
      if (freeDigital) return freeDigital.id;
    }
    return products[0]?.id;
  });
  // Fall back rather than throw: `useCanUseFreeCards` reads true until the event lands, so a
  // new-stack wedding can seed the free digital selection and then lose that product (MGT-109).
  const selectedProductObject = products.find(p => p.id === selectedProductId) ?? products[0];

  if (!selectedProductObject) {
    throw new Error('No product pricing found.');
  }

  const resolvedProductId = selectedProductObject.id;
  const cardFormat = convertProductTypeToCardFormat(selectedProductObject.productType);

  const [storedTemplate, setStoredTemplate] = useState<StationeryTemplatePDP | null>(template);

  const handleOnPaperCustomization = useEventCallback((draft: DraftData) => {
    setStoredTemplate(prev =>
      prev
        ? {
            ...prev,
            themeJSON: draft.config,
          }
        : null,
    );
  });

  const currentProduct = selectedProductObject;

  const { isCreatingDraft, onCustomizeClick } = useCustomizeClickHandler({
    currentCategory: templateCategory,
    eventId,
    onCustomizeCardClickOverride,
    supportedFormats: supportedTemplateFormats,
    currentProduct: currentProduct,
  });

  const handleOnCustomizeDigitalClick = useEventCallback(() => {
    if (digitalDesignTemplate) {
      onCustomizeClick('digital', digitalDesignTemplate, supportedTemplateFormats);
    }
  }, [template, digitalDesignTemplate]);

  const isMobileOrTablet = useIsMobileOrTablet();
  // Will be attached to the `CustomizeNowButton` in the customization pane
  // Used to determine when to show the button in the header
  const intersectionRef = useRef<HTMLButtonElement>(null);
  const shouldShowCustomizeButtonInHeader = useStickyCustomizeButton(intersectionRef, !isMobileOrTablet);

  const handleSetProductID = useEventCallback((newProductId: string) => {
    setSelectedProductId(newProductId);
    withWindow(global => {
      global.window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    buttonInteracted({ name: 'pdpModal_formatControlClicked', themeId, format: newProductId });
  });

  // Stable reference: a fresh object every render would re-run every
  // downstream memo in ProductSizeSelector (options, price calculator).
  const currentTemplate = useMemo(() => (template ? { id: template.id, widthInches: template.widthInches, heightInches: template.heightInches } : null), [template]);

  const productSelector: StationeryProductSelector = {
    onChange: handleSetProductID,
    cardFormat: cardFormat,
    productId: resolvedProductId,
    currentTemplate,
  };

  const basePdpProps: BasePdpImplementationProps = {
    productSelector,
    designName,
    intersectionRef,
    isCreatingDraft,
    showCustomizeButtonInHeader: shouldShowCustomizeButtonInHeader,
    hideCustomizeButtonInCustomizationPane: isMobileOrTablet,
    templateCategory,
    themeId,
    isFoil: template ? isFoilTemplate(template) : false,
    isBasicDigitalV2Enabled,
  };

  return (
    <StationeryProductPricingContextProvider
      products={products}
      activeProductId={resolvedProductId}
    >
      <PdpColorwayProvider
        page={storedTemplate?.themeJSON.card.front}
        themeId={themeId}
        templateId={template?.id}
      >
        {cardFormat === CardFormat.paper && storedTemplate && (
          <PaperPdp
            eventId={eventId}
            template={storedTemplate}
            onPaperCustomization={handleOnPaperCustomization}
            onCustomizeClick={t => onCustomizeClick('paper', t, supportedTemplateFormats)}
            {...basePdpProps}
          />
        )}

        {cardFormat === CardFormat.premiumDigital && storedTemplate && (
          <PremiumDigitalPdp
            eventId={eventId}
            template={storedTemplate}
            onCustomizeClick={t => onCustomizeClick('premiumDigital', t, supportedTemplateFormats)}
            {...basePdpProps}
          />
        )}

        {cardFormat === CardFormat.basicDigitalV2 && storedTemplate && (
          <BasicDigitalV2Pdp
            eventId={eventId}
            template={storedTemplate}
            onCustomizeClick={t => onCustomizeClick('basicDigitalV2', t, supportedTemplateFormats)}
            {...basePdpProps}
          />
        )}

        {/* BasicDigitalPdp renders from `digitalDesignTemplate` alone (no `storedTemplate` needed),
            so digital-only themes — which have no printable paper template — render here too. */}
        {cardFormat === CardFormat.digital && digitalDesignTemplate && (
          <BasicDigitalPdp
            eventId={eventId}
            template={digitalDesignTemplate}
            onCustomizeDigitalClick={handleOnCustomizeDigitalClick}
            {...basePdpProps}
          />
        )}
      </PdpColorwayProvider>
    </StationeryProductPricingContextProvider>
  );
};

export const ProductDetailsPage = wrapWithStationeryProductDetailsHealthProvider((props: ProductDetailsPageProps) => {
  const { currentCategory } = useCardCategoryContext();
  const { themeId, templateId } = props;
  const { template, digitalTemplate, loading } = usePdpData({
    themeId,
    category: currentCategory,
    templateId,
    enableRedirectIfInvalid: true,
    enableUpdateUrlToIncludeTemplateId: true,
  });

  const { isEnabled: isBasicDigitalV2Enabled, loading: basicDigitalV2Loading } = useIsBasicDigitalV2Enabled({
    hasBasicDigitalV2Product: !!template?.products.some(isBasicDigitalV2),
  });

  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });

  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);

  // MGT-109: a digital-only design (ecard or photo save-the-date) has no printable template, so the
  // free ecard is its only product. New-stack weddings don't support free cards (Evan, 2026-09-30),
  // so the design isn't offered there at all -- back to the category shop, which does serve them.
  const { getShopPath } = useCardsRouterContext();
  const history = useHistory();
  const eventInfoResult = useEventInfo();
  const canUseFreeCards = useCanUseFreeCards();
  const isDigitalOnlyDesign = !template && !!digitalTemplate;
  // `useCanUseFreeCards` reads true until the event lands, so only a digital-only design waits on
  // it; every other PDP renders on exactly the timing it does today.
  const freeCardsDecisionPending = isDigitalOnlyDesign && isEventReadPending(eventInfoResult);
  const hideDigitalOnlyDesign = isDigitalOnlyDesign && !freeCardsDecisionPending && !canUseFreeCards;

  useEffect(() => {
    // No telemetry: the design is valid and merely unsold here. Replace, so Back doesn't loop on this page.
    if (hideDigitalOnlyDesign) history.replace(getShopPath('wedding', currentCategory));
  }, [hideDigitalOnlyDesign, getShopPath, history, currentCategory]);

  // Extracting design name here for consistency between pdps
  const designName = (template && templateToTitle(template)) || digitalTemplate?.name || '';

  useFreeShippingBanner(template?.products ?? []);

  return (
    <>
      <PdpSeo
        themeId={themeId}
        currentCategory={currentCategory}
      />
      <SkeletonGroup
        // Ready when a printable template has products, OR a digital-only theme (no
        // printable template at all) has a digital design. A template that IS present but
        // has empty products stays in the skeleton rather than mounting Root into a
        // productless state (which would throw or silently hide the paper format).
        // `basicDigitalV2Loading` is included so Root seeds its selected product from the
        // final list: the flag filters the free product out, so resolving it after that
        // first render would strand a `?format=basicDigitalV2` deep link on the paper tab.
        // `freeCardsDecisionPending`/`hideDigitalOnlyDesign` keep the skeleton up while the event
        // read settles and across the redirect, so a new-stack couple never sees the free PDP.
        isReady={!loading && !basicDigitalV2Loading && !freeCardsDecisionPending && !hideDigitalOnlyDesign && (!!template?.products.length || (!template && !!digitalTemplate))}
        placeholder={<PdpLayout.Skeleton />}
        width="100%"
      >
        {template || digitalTemplate ? (
          <Root
            key={templateId}
            template={template}
            digitalDesignTemplate={digitalTemplate}
            designName={designName}
            templateCategory={currentCategory}
            isBasicDigitalV2Enabled={isBasicDigitalV2Enabled}
            {...props}
          />
        ) : null}
      </SkeletonGroup>
    </>
  );
});
